import React, { useState, useEffect } from 'react';
import { Table, Button, Badge, Form, InputGroup, Modal, Spinner, Alert } from 'react-bootstrap';
import { FaSearch, FaUserPlus, FaKey, FaCheckCircle, FaTimesCircle, FaEllipsisV } from 'react-icons/fa';
import { dsoApi } from '../../services/api';
import './Backoffice.css';

function DSOBackoffice() {
  const [backoffices, setBackoffices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [totalBackoffices, setTotalBackoffices] = useState(0);
  
  // State for the add backoffice modal
  const [showAddModal, setShowAddModal] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    mobile: '',
    password: '',
    confirmPassword: ''
  });
  const [formErrors, setFormErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [submitSuccess, setSubmitSuccess] = useState('');
  
  // State for status toggle
  const [toggleLoading, setToggleLoading] = useState(false);
  
  // State for reset password modal
  const [showResetModal, setShowResetModal] = useState(false);
  const [selectedBackoffice, setSelectedBackoffice] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [confirmNewPassword, setConfirmNewPassword] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [resetLoading, setResetLoading] = useState(false);
  const [resetSuccess, setResetSuccess] = useState('');

  useEffect(() => {
    fetchBackoffices();
  }, []);

  const fetchBackoffices = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await dsoApi.getBackoffices();
      console.log('Backoffices fetched:', response.data);
      setBackoffices(response.data.items || []);
      setTotalBackoffices(response.data.total || 0);
    } catch (err) {
      console.error('Error fetching backoffices:', err);
      setError('Failed to load backoffice users. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    fetchBackoffices();
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
    
    // Clear the error for this field as user types
    if (formErrors[name]) {
      setFormErrors({
        ...formErrors,
        [name]: ''
      });
    }
  };

  const validateForm = () => {
    const errors = {};
    
    if (!formData.name.trim()) {
      errors.name = 'Name is required';
    }
    
    if (!formData.email.trim()) {
      errors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      errors.email = 'Email is invalid';
    }
    
    if (!formData.mobile.trim()) {
      errors.mobile = 'Mobile number is required';
    } else if (!/^\d{10}$/.test(formData.mobile.replace(/\D/g, ''))) {
      errors.mobile = 'Mobile number must be 10 digits';
    }
    
    if (!formData.password) {
      errors.password = 'Password is required';
    } else if (formData.password.length < 6) {
      errors.password = 'Password must be at least 6 characters';
    }
    
    if (formData.password !== formData.confirmPassword) {
      errors.confirmPassword = 'Passwords do not match';
    }
    
    return errors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitError('');
    setSubmitSuccess('');
    
    // Validate form
    const errors = validateForm();
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }
    
    setSubmitting(true);
    try {
      // Prepare data for submission (exclude confirmPassword)
      const submitData = {
        name: formData.name,
        email: formData.email,
        mobile: formData.mobile,
        password: formData.password
      };
      
      const response = await dsoApi.createBackoffice(submitData);
      console.log('Backoffice created:', response.data);
      setSubmitSuccess('Backoffice user created successfully!');
      
      // Reset form
      setFormData({
        name: '',
        email: '',
        mobile: '',
        password: '',
        confirmPassword: ''
      });
      
      // Refresh the backoffices list
      fetchBackoffices();
      
      // Close modal after a short delay
      setTimeout(() => {
        setShowAddModal(false);
        setSubmitSuccess('');
      }, 1500);
      
    } catch (err) {
      console.error('Error creating backoffice:', err);
      setSubmitError(err.response?.data?.detail || 'Failed to create backoffice user. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleStatusToggle = async (backofficeId, currentStatus) => {
    setToggleLoading(backofficeId);
    try {
      await dsoApi.toggleBackofficeStatus(backofficeId, !currentStatus);
      // Update the backoffice in the list
      setBackoffices(backoffices.map(backoffice => {
        if (backoffice.user_id === backofficeId) {
          return { ...backoffice, is_active: !currentStatus };
        }
        return backoffice;
      }));
    } catch (err) {
      console.error('Error toggling backoffice status:', err);
      alert('Failed to update backoffice status. Please try again.');
    } finally {
      setToggleLoading(false);
    }
  };
  
  const openResetPasswordModal = (backoffice) => {
    setSelectedBackoffice(backoffice);
    setNewPassword('');
    setConfirmNewPassword('');
    setPasswordError('');
    setResetSuccess('');
    setShowResetModal(true);
  };
  
  const handleResetPassword = async (e) => {
    e.preventDefault();
    setPasswordError('');
    setResetSuccess('');
    
    // Validate passwords
    if (!newPassword) {
      setPasswordError('Password is required');
      return;
    }
    
    if (newPassword.length < 6) {
      setPasswordError('Password must be at least 6 characters');
      return;
    }
    
    if (newPassword !== confirmNewPassword) {
      setPasswordError('Passwords do not match');
      return;
    }
    
    setResetLoading(true);
    try {
      // Using the dsoApi to reset the backoffice user's password
      // We should add this method to the api.js file if it doesn't exist yet
      await dsoApi.resetBackofficePassword(selectedBackoffice.user_id, newPassword);
      setResetSuccess('Password has been reset successfully');
      
      // Close modal after a short delay
      setTimeout(() => {
        setShowResetModal(false);
        setResetSuccess('');
      }, 1500);
    } catch (err) {
      console.error('Error resetting password:', err);
      setPasswordError(err.response?.data?.detail || 'Failed to reset password. Please try again.');
    } finally {
      setResetLoading(false);
    }
  };

  return (
    <div className="dso-backoffice-container">
      <div className="dso-backoffice-header">
        <h3>Backoffice Management</h3>
        <div className="dso-backoffice-actions">
          <Button onClick={() => setShowAddModal(true)}>
            <FaUserPlus className="me-1" /> Add Backoffice
          </Button>
        </div>
      </div>

      <div className="dso-backoffice-filters">
        <Form onSubmit={handleSearch} className="dso-search-form">
          <InputGroup>
            <Form.Control
              placeholder="Search backoffices..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            <Button variant="outline-secondary" type="submit">
              <FaSearch />
            </Button>
          </InputGroup>
        </Form>
      </div>

      {error && (
        <div className="alert alert-danger mt-3">
          {error}
        </div>
      )}

      {loading ? (
        <div className="dso-backoffice-loading">
          <Spinner animation="border" role="status">
            <span className="visually-hidden">Loading...</span>
          </Spinner>
        </div>
      ) : (
        <>
          {backoffices.length === 0 ? (
            <div className="dso-backoffice-empty">
              <p>No backoffice users found. Click "Add Backoffice" to create one.</p>
            </div>
          ) : (
            <div className="dso-backoffice-table-container">
              <Table responsive striped className="dso-backoffice-table">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Email</th>
                    <th>Phone</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {backoffices.map((backoffice) => (
                    <tr key={backoffice.user_id}>
                      <td>{backoffice.name}</td>
                      <td>{backoffice.email}</td>
                      <td>{backoffice.mobile}</td>
                      <td>
                        <Badge 
                          bg={backoffice.is_active ? 'success' : 'danger'}
                          className="dso-backoffice-status"
                        >
                          {backoffice.is_active ? 'Active' : 'Inactive'}
                        </Badge>
                      </td>
                      <td>
                        <div className="dso-backoffice-actions">
                          <Button 
                            variant={backoffice.is_active ? 'outline-danger' : 'outline-success'} 
                            size="sm"
                            onClick={() => handleStatusToggle(backoffice.user_id, backoffice.is_active)}
                            disabled={toggleLoading === backoffice.user_id}
                          >
                            {toggleLoading === backoffice.user_id ? (
                              <Spinner 
                                as="span" 
                                animation="border" 
                                size="sm" 
                                role="status" 
                                aria-hidden="true"
                              />
                            ) : backoffice.is_active ? (
                              <>
                                <FaTimesCircle className="me-1" /> Deactivate
                              </>
                            ) : (
                              <>
                                <FaCheckCircle className="me-1" /> Activate
                              </>
                            )}
                          </Button>
                          <Button 
                            variant="outline-primary" 
                            size="sm"
                            className="ms-2"
                            onClick={() => openResetPasswordModal(backoffice)}
                          >
                            <FaKey className="me-1" /> Reset Password
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          )}
        </>
      )}

      {/* Add Backoffice Modal */}
      <Modal show={showAddModal} onHide={() => setShowAddModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Add New Backoffice User</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {submitError && <Alert variant="danger">{submitError}</Alert>}
          {submitSuccess && <Alert variant="success">{submitSuccess}</Alert>}
          
          <Form onSubmit={handleSubmit}>
            <Form.Group className="mb-3">
              <Form.Label>Name</Form.Label>
              <Form.Control
                type="text"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                isInvalid={!!formErrors.name}
                disabled={submitting}
              />
              <Form.Control.Feedback type="invalid">
                {formErrors.name}
              </Form.Control.Feedback>
            </Form.Group>
            
            <Form.Group className="mb-3">
              <Form.Label>Email</Form.Label>
              <Form.Control
                type="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                isInvalid={!!formErrors.email}
                disabled={submitting}
              />
              <Form.Control.Feedback type="invalid">
                {formErrors.email}
              </Form.Control.Feedback>
            </Form.Group>
            
            <Form.Group className="mb-3">
              <Form.Label>Mobile Number</Form.Label>
              <Form.Control
                type="text"
                name="mobile"
                value={formData.mobile}
                onChange={handleInputChange}
                isInvalid={!!formErrors.mobile}
                disabled={submitting}
              />
              <Form.Control.Feedback type="invalid">
                {formErrors.mobile}
              </Form.Control.Feedback>
            </Form.Group>
            
            <Form.Group className="mb-3">
              <Form.Label>Password</Form.Label>
              <Form.Control
                type="password"
                name="password"
                value={formData.password}
                onChange={handleInputChange}
                isInvalid={!!formErrors.password}
                disabled={submitting}
              />
              <Form.Control.Feedback type="invalid">
                {formErrors.password}
              </Form.Control.Feedback>
            </Form.Group>
            
            <Form.Group className="mb-3">
              <Form.Label>Confirm Password</Form.Label>
              <Form.Control
                type="password"
                name="confirmPassword"
                value={formData.confirmPassword}
                onChange={handleInputChange}
                isInvalid={!!formErrors.confirmPassword}
                disabled={submitting}
              />
              <Form.Control.Feedback type="invalid">
                {formErrors.confirmPassword}
              </Form.Control.Feedback>
            </Form.Group>
            
            <div className="d-flex justify-content-end">
              <Button 
                variant="secondary" 
                className="me-2"
                onClick={() => setShowAddModal(false)}
                disabled={submitting}
              >
                Cancel
              </Button>
              <Button 
                variant="primary" 
                type="submit"
                disabled={submitting}
              >
                {submitting ? (
                  <>
                    <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" /> Creating...
                  </>
                ) : 'Create Backoffice'}
              </Button>
            </div>
          </Form>
        </Modal.Body>
      </Modal>

      {/* Reset Password Modal */}
      <Modal show={showResetModal} onHide={() => setShowResetModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Reset Password</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {passwordError && <Alert variant="danger">{passwordError}</Alert>}
          {resetSuccess && <Alert variant="success">{resetSuccess}</Alert>}
          
          {selectedBackoffice && (
            <p>Reset password for <strong>{selectedBackoffice.name}</strong> ({selectedBackoffice.email})</p>
          )}
          
          <Form onSubmit={handleResetPassword}>
            <Form.Group className="mb-3">
              <Form.Label>New Password</Form.Label>
              <Form.Control
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                disabled={resetLoading}
              />
            </Form.Group>
            
            <Form.Group className="mb-3">
              <Form.Label>Confirm New Password</Form.Label>
              <Form.Control
                type="password"
                value={confirmNewPassword}
                onChange={(e) => setConfirmNewPassword(e.target.value)}
                disabled={resetLoading}
              />
            </Form.Group>
            
            <div className="d-flex justify-content-end">
              <Button 
                variant="secondary" 
                className="me-2"
                onClick={() => setShowResetModal(false)}
                disabled={resetLoading}
              >
                Cancel
              </Button>
              <Button 
                variant="primary" 
                type="submit"
                disabled={resetLoading}
              >
                {resetLoading ? (
                  <>
                    <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" /> Resetting...
                  </>
                ) : 'Reset Password'}
              </Button>
            </div>
          </Form>
        </Modal.Body>
      </Modal>
    </div>
  );
}

export default DSOBackoffice;